/**
 * @file   src/helpers/priceCapUtils.js
 * @brief  Price cap, formatting, and demand analysis utilities
 */
import {
  BUY,
  LONG_RENT,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
} from "../constants/common";

// --- Buyers tab: smart cap helpers ---

/**
 * Round a value UP to the nearest "nice" number for display.
 */
const roundNice = (value, precision) => {
  return Math.ceil(value / precision) * precision;
};

/**
 * Get the default max budget for an ad type.
 */
const getDefaultMax = (adType) => {
  if (adType === BUY) return DEFAULT_MAX_BUDGET_BUY;
  if (adType === LONG_RENT) return DEFAULT_MAX_BUDGET_LTR;
  return DEFAULT_MAX_BUDGET_STR;
};

/**
 * Round P95 to a "nice" display number based on ad type.
 */
const roundCapNice = (p95, adType) => {
  if (adType === BUY) {
    if (p95 < 1000000) return roundNice(p95, 25000);
    if (p95 < 5000000) return roundNice(p95, 50000);
    return roundNice(p95, 100000);
  }
  if (adType === LONG_RENT) {
    return roundNice(p95, 100);
  }
  return roundNice(p95, 50);
};

/**
 * Compute dynamic step proportional to cap.
 */
const computeStep = (cap, adType) => {
  if (adType === BUY) {
    if (cap <= 500000) return 5000;
    if (cap <= 2000000) return 10000;
    if (cap <= 5000000) return 25000;
    return 50000;
  }
  if (adType === LONG_RENT) {
    return Math.max(10, roundNice(cap / 100, 10));
  }
  return Math.max(5, roundNice(cap / 100, 5));
};

/**
 * Calculate a smart price cap from price data distribution.
 * Used on the Buyers tab histogram slider.
 */
export const calculateSmartCap = (prices, adType) => {
  const defaultMax = getDefaultMax(adType);

  if (!prices || prices.length < 5) {
    return {
      cap: defaultMax,
      step: computeStep(defaultMax, adType),
      fallback: true,
    };
  }

  const sorted = [...prices].sort((a, b) => a - b);

  const p95Index = Math.floor(sorted.length * 0.95);
  const p95Value = sorted[Math.min(p95Index, sorted.length - 1)];

  const cap = roundCapNice(p95Value, adType);
  const step = computeStep(cap, adType);

  return { cap, step, fallback: false };
};

// --- Match tab: demand analysis helpers ---

/**
 * Format a price into a compact human-readable string.
 * Examples: 500000 -> "$500K", 1200000 -> "$1.2M", 750 -> "$750"
 */
export const formatCompactPrice = (value) => {
  if (value == null || isNaN(value)) return "$0";
  const num = Math.abs(value);
  if (num >= 1000000) {
    const millions = num / 1000000;
    const formatted = millions % 1 === 0
      ? millions.toFixed(0)
      : millions.toFixed(1);
    return `$${formatted}M`;
  }
  if (num >= 1000) {
    const thousands = num / 1000;
    const formatted = thousands % 1 === 0
      ? thousands.toFixed(0)
      : thousands.toFixed(1);
    return `$${formatted}K`;
  }
  return `$${Math.round(num)}`;
};

/**
 * Find the bin index with the highest count (peak demand).
 */
export const computePeakDemandBin = (prices, numBins, min, max) => {
  if (!prices || prices.length < 3 || !max || max <= 0 || !numBins) return null;
  const rangeMin = min || 0;
  const span = max - rangeMin;
  if (span <= 0) return null;

  const binWidth = span / numBins;
  const bins = new Array(numBins).fill(0);

  prices.forEach((price) => {
    let idx = Math.floor((price - rangeMin) / binWidth);
    if (idx >= numBins) idx = numBins - 1;
    if (idx < 0) idx = 0;
    bins[idx]++;
  });

  let peakIdx = 0;
  for (let i = 1; i < numBins; i++) {
    if (bins[i] > bins[peakIdx]) peakIdx = i;
  }

  return bins[peakIdx] > 0 ? peakIdx : null;
};

/**
 * Compute the price range where most buyer demand concentrates.
 * Returns the range covering the middle 60% of price data.
 */
export const computeSweetSpotRange = (prices, adType) => {
  if (!prices || prices.length < 3) return null;

  const sorted = [...prices].sort((a, b) => a - b);

  const lowIdx = Math.floor(sorted.length * 0.2);
  const highIdx = Math.floor(sorted.length * 0.8);
  const rawMin = sorted[lowIdx];
  const rawMax = sorted[Math.min(highIdx, sorted.length - 1)];

  let roundTo;
  if (adType === BUY) {
    roundTo = rawMin < 500000 ? 25000 : 50000;
  } else if (adType === LONG_RENT) {
    roundTo = 100;
  } else {
    roundTo = 50;
  }

  const min = Math.floor(rawMin / roundTo) * roundTo;
  const max = Math.ceil(rawMax / roundTo) * roundTo;

  return { min, max };
};

// --- Shared: property price range ---

/**
 * Calculate slider range centered on a property's asking price with tiered padding.
 * Used on the Match tab when a property is selected.
 */
export const calculatePropertyPriceRange = (price, adType) => {
  if (!price || price <= 0) return null;

  let padding, step;

  if (adType === BUY) {
    if (price < 300000) {
      padding = 50000;
      step = 5000;
    } else if (price < 1000000) {
      padding = 100000;
      step = 10000;
    } else if (price < 3000000) {
      padding = 500000;
      step = 25000;
    } else {
      padding = 1000000;
      step = 50000;
    }
  } else if (adType === LONG_RENT) {
    if (price < 2000) {
      padding = 500;
      step = 50;
    } else if (price < 5000) {
      padding = 1000;
      step = 100;
    } else {
      padding = 2000;
      step = 200;
    }
  } else {
    // STR (nightly)
    if (price < 200) {
      padding = 50;
      step = 10;
    } else if (price < 500) {
      padding = 100;
      step = 25;
    } else {
      padding = 200;
      step = 50;
    }
  }

  const rangeMin = Math.max(0, Math.floor((price - padding) / step) * step);
  const rangeMax = Math.ceil((price + padding) / step) * step;

  return { min: rangeMin, max: rangeMax, step };
};
